import { type Ref, watch } from "vue"
import { useManualTracking } from "./reactivity"

export namespace Position {
  export type Snap = 'bottom left' | 'bottom' | 'bottom right' | 'top left' | 'top' | 'top right' | 'right' | 'left'

  export type LTRB = {
    [K in 'right' | 'left' | 'top' | 'bottom']?: string
  }

  export type Axis = 'horizontal' | 'vertical'
}

export type PositionMethod<T> = (rect: DOMRect, props: T) => Position.LTRB

export function useAnimationFramePosition <T> (isOpenRef: Ref<boolean>, positionMethod: () => T | never) {
  const { computedRef: position, recompute: recomputePosition } = useManualTracking(positionMethod)

  watch(isOpenRef, (value) => {
    if (value) {
      const animationFrameCallback = () => {
        recomputePosition()
  
        if (isOpenRef.value) {
          window.requestAnimationFrame(animationFrameCallback)
        }
      }
  
      window.requestAnimationFrame(animationFrameCallback)
    }
  })

  return position
}

export function getDOMRect (element: Element | undefined | null): DOMRect | never
export function getDOMRect (element: Element | undefined | null, required: false): DOMRect | undefined
export function getDOMRect (element: Element | undefined | null, required: boolean = true): DOMRect | undefined | never {
  const rect = element?.getBoundingClientRect()

  if (rect) {
    return rect
  } else if (required) {
    throw new Error('Element is not visible')
  }
}

const MeasuringNode: {
  node: HTMLElement | null
  get (): HTMLElement
} = {
  node: null,
  get () {
    this.node ||= (() => {
      // Create div that is outside of window & is not interactable
      const element = document.createElement('div')
      element.style.position = 'fixed'
      element.style.left = '101vw'
      element.style.zIndex = '-1'
      element.style.visibility = 'hidden'
      element.style.pointerEvents = 'none'

      document.body.appendChild(element)

      return element
    })()

    return this.node
  }
}

export function getTextRect (text: string, classList: string) {
  const element = MeasuringNode.get()

  element.classList = classList
  element.innerText = text

  return getDOMRect(element)
}